import React, { useState, useMemo } from 'react';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import { calculateScenario, optimizeEmployee } from '../utils/calculations';
import { getApplicableMinimumWage, getLocalTaxEntry } from '../utils/jurisdictionResolution';
import { fmt } from '../utils/formatters';

export default function CalculationAudit({ 
  salary, 
  state, 
  filing, 
  selectedPlan, 
  config,
  currentUser,
  employeeContext = null,
}) {
  const [expanded, setExpanded] = useState(false);

  // Get all active plans for optimization step
  const activePlans = (config?.planTiers || []).filter(tier => !tier.archived);

  // Calculate full optimization to show all plans - MUST be called before any returns
  const allPlanResults = useMemo(() => {
    if (!selectedPlan || !config) return [];
    
    return activePlans.map(tier => {
      const calc = calculateScenario(
        salary,
        state,
        filing,
        tier.monthlyPremium,
        tier.monthlyClaim,
        config,
        config.engineSettings?.payPeriodsPerYear || 26,
        employeeContext || { state }
      );
      const annualPremium = tier.monthlyPremium * 12;
      const residual = salary - annualPremium;
      const annualHours = config.engineSettings?.annualHoursForMinWage || 2080;
      const hourly = residual / annualHours;
      const minWageInfoLocal = getApplicableMinimumWage(employeeContext || { state }, config);
      const minWage = Number(minWageInfoLocal.applicable) || (config.payrollConstants?.minWageHourly || 7.25);
      const wageOk = hourly >= minWage && residual > 0;
      const netPositive = calc.diff.netTakeHome >= 0;
      const viable = wageOk && netPositive;

      return {
        ...tier,
        calc,
        annualNet: calc.diff.netTakeHome,
        viable,
        wageOk,
        netPositive
      };
    }).sort((a, b) => b.annualNet - a.annualNet);
  }, [salary, state, filing, config, activePlans, selectedPlan]);

  // Only show to super users - AFTER all hooks
  if (!currentUser || currentUser.role !== 'super_user') {
    return null;
  }

  if (!selectedPlan || !config) {
    return null;
  }

  // Current plan calculation
  const calc = calculateScenario(
    salary,
    state,
    filing,
    selectedPlan.monthlyPremium,
    selectedPlan.monthlyClaim,
    config,
    config.engineSettings?.payPeriodsPerYear || 26,
    employeeContext || { state }
  );

  // Safety check - if calc is invalid, don't render
  if (!calc || !calc.with125 || !calc.without || !calc.diff) {
    return null;
  }

  const annualPremium = selectedPlan.monthlyPremium * 12;
  const annualClaim = selectedPlan.monthlyClaim * 12;
  const annualSpread = annualPremium - annualClaim;

  // Get tax data first
  const standardDeduction = config.payrollConstants?.[`standardDeduction_${filing}`] || 0;

  // Calculate taxable incomes for display
  const federalTaxableWithout = Math.max(0, salary - standardDeduction);
  const federalTaxableWith = Math.max(0, (salary - annualPremium) - standardDeduction);

  // Get more tax data
  const federalBrackets = config.federalBrackets?.[filing] || [];
  const ssRate = config.payrollConstants?.ssRate || 0.062;
  const ssWageBase = config.payrollConstants?.ssWageBase || 168600;
  const medicareRate = config.payrollConstants?.medicareRate || 0.0145;
  const addlMedicareRate = config.payrollConstants?.addlMedicareRate || 0.009;
  const addlMedicareThreshold = config.payrollConstants?.addlMedicareThresholds?.[filing] || 200000;
  const employerFicaRate = (config.payrollConstants?.ssRate || 0.062) + (config.payrollConstants?.medicareRate || 0.0145);

  const stateData = config.stateData?.[state] || {};
  const stateName = stateData.name || state;
  const stateConforms = stateData.conformsTo125 !== false;
  const stateBrackets = stateData.rates || [];
  const sdiRate = stateData.sdi || 0;
  const pfmlRate = stateData.pfml || 0;
  const fliRate = stateData.fli || 0;

  // Phase 17: jurisdiction-aware min wage + local-tax info
  const minWageInfo = getApplicableMinimumWage(employeeContext || { state }, config);
  const minWage = Number(minWageInfo.applicable) || (config.payrollConstants?.minWageHourly || 7.25);
  const localTaxResolution = getLocalTaxEntry(employeeContext || { state }, config);
  const localTaxEntry = localTaxResolution?.entry || null;
  const annualHours = config.engineSettings?.annualHoursForMinWage || 2080;

  const residualIncome = salary - annualPremium;
  const effectiveHourly = residualIncome / annualHours;
  const wageOk = effectiveHourly >= minWage && residualIncome > 0;
  const netPositive = calc.diff.netTakeHome >= 0;
  const viable = wageOk && netPositive;

  const filingLabels = { single: 'Single (S)', married: 'Married (M)', hoh: 'Head of Household (HOH)' };

  const lastSyncDate = config.lastTaxSync?.appliedAt 
    ? new Date(config.lastTaxSync.appliedAt).toLocaleDateString()
    : 'N/A';

  return (
    <div style={S.card}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <h3 style={{ ...S.heading, margin: 0 }}>
          🔍 How's This Work? — Calculation Audit
        </h3>
        <Icon 
          name={expanded ? 'chevron-up-outline' : 'chevron-down-outline'} 
          style={{ fontSize: 20, color: C.textMid }} 
        />
      </button>

      {expanded && (
        <div style={{ 
          marginTop: 16, 
          borderLeft: `3px solid ${C.accent}`, 
          paddingLeft: 16 
        }}>
          {/* Context Banner */}
          <div style={{
            padding: 12,
            background: C.accentBg,
            border: `1px solid ${C.accent}`,
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 13,
            color: C.text
          }}>
            This shows the exact calculation for <strong>{stateName}</strong> at{' '}
            <strong>{fmt(salary)}</strong> salary, <strong>{filingLabels[filing]}</strong> filing,
            on <strong>{selectedPlan.label}</strong>. All calculations are annual. 
            Monthly figures are annual ÷ 12.
          </div>

          {/* Step 1: Input Parameters */}
          <StepSection
            number={1}
            title="Input Parameters"
            content={
              <div style={{ fontSize: 13, ...S.mono }}>
                <Row label="Annual Salary:" value={fmt(salary)} note="as entered" />
                <Row label="State:" value={`${state} — ${stateName}`} note={stateConforms ? '§125 conforms' : 'non-conforming'} />
                <Row label="Filing Status:" value={filingLabels[filing]} />
                <Row label="Plan Tier:" value={selectedPlan.label} />
                <Row label="Monthly Premium:" value={fmt(selectedPlan.monthlyPremium)} note={`× 12 = ${fmt(annualPremium)}/yr`} />
                <Row label="Monthly Claim:" value={fmt(selectedPlan.monthlyClaim)} note={`× 12 = ${fmt(annualClaim)}/yr`} />
                <Row label="Spread (Prem − Claim):" value={`${fmt(selectedPlan.spread)}/mo`} note={`${fmt(annualSpread)}/yr — net cost`} highlight />
              </div>
            }
          />

          {/* Step 2: Federal Income Tax */}
          <StepSection
            number={2}
            title="Federal Income Tax"
            content={
              <div>
                <FormulaBox>
                  Taxable Income = Salary − Standard Deduction
                </FormulaBox>
                
                <ComparisonTable>
                  <ComparisonRow 
                    label="Standard Deduction" 
                    without={fmt(standardDeduction)} 
                    with={fmt(standardDeduction)} 
                  />
                  <ComparisonRow 
                    label="Taxable Income" 
                    without={fmt(federalTaxableWithout)} 
                    with={fmt(federalTaxableWith)} 
                  />
                  <ComparisonRow 
                    label="Federal Income Tax" 
                    without={fmt(calc.without.federalTax * 12)} 
                    with={fmt(calc.with125.federalTax * 12)} 
                    highlight
                  />
                </ComparisonTable>

                <SavingsBox value={calc.diff.federalTax * 12} label="Federal Income Tax Savings" />

                <div style={{ marginTop: 12, fontSize: 12, color: C.textMid }}>
                  <strong>Brackets used:</strong>
                  <div style={{ marginTop: 4 }}>
                    {federalBrackets.map((b, i) => (
                      <div key={i}>
                        {(b.rate * 100).toFixed(1)}% on {fmt(b.min)}–{b.max ? fmt(b.max) : '∞'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            }
          />

          {/* Step 3: Social Security Tax */}
          <StepSection
            number={3}
            title="Social Security Tax"
            content={
              <div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  Rate: <strong>{(ssRate * 100).toFixed(2)}%</strong> | 
                  Wage Base Cap: <strong>{fmt(ssWageBase)}</strong>
                </div>
                
                <ComparisonTable>
                  <ComparisonRow 
                    label="Wages Subject to SS" 
                    without={fmt(Math.min(salary, ssWageBase))} 
                    with={fmt(Math.min(salary - annualPremium, ssWageBase))} 
                  />
                  <ComparisonRow 
                    label="Social Security Tax" 
                    without={fmt(calc.without.socialSecurity * 12)} 
                    with={fmt(calc.with125.socialSecurity * 12)} 
                    highlight
                  />
                </ComparisonTable>

                <SavingsBox value={calc.diff.socialSecurity * 12} label="Social Security Savings" />

                {salary > ssWageBase && (
                  <div style={{ marginTop: 8, fontSize: 12, color: C.textMid, fontStyle: 'italic' }}>
                    Note: Employee is above SS wage base cap
                  </div>
                )}
              </div>
            }
          />

          {/* Step 4: Medicare Tax */}
          <StepSection
            number={4}
            title="Medicare Tax"
            content={
              <div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  Base Rate: <strong>{(medicareRate * 100).toFixed(2)}%</strong> | 
                  Additional Rate: <strong>{(addlMedicareRate * 100).toFixed(2)}%</strong> above{' '}
                  <strong>{fmt(addlMedicareThreshold)}</strong>
                </div>
                
                <ComparisonTable>
                  <ComparisonRow 
                    label="Medicare Tax" 
                    without={fmt(calc.without.medicare * 12)} 
                    with={fmt(calc.with125.medicare * 12)} 
                    highlight
                  />
                </ComparisonTable>

                <SavingsBox value={calc.diff.medicare * 12} label="Medicare Savings" />
              </div>
            }
          />

          {/* Step 5: State Income Tax */}
          <StepSection
            number={5}
            title="State Income Tax"
            content={
              <div>
                {!stateConforms && (
                  <div style={{
                    padding: 10,
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: 6,
                    marginBottom: 12,
                    fontSize: 12,
                    color: '#92400e'
                  }}>
                    ⚠️ <strong>Non-conforming state:</strong> {stateName} does not allow pre-tax §125 premium deductions. 
                    State tax is calculated on full salary.
                  </div>
                )}
                
                <ComparisonTable>
                  <ComparisonRow 
                    label="State Taxable Income" 
                    without={fmt(salary)} 
                    with={fmt(stateConforms ? (salary - annualPremium) : salary)} 
                  />
                  <ComparisonRow 
                    label="State Income Tax" 
                    without={fmt(calc.without.stateTax * 12)} 
                    with={fmt(calc.with125.stateTax * 12)} 
                    highlight
                  />
                </ComparisonTable>

                <SavingsBox value={calc.diff.stateTax * 12} label="State Income Tax Savings" />

                {stateBrackets.length > 0 && (
                  <div style={{ marginTop: 12, fontSize: 12, color: C.textMid }}>
                    <strong>State brackets:</strong>
                    <div style={{ marginTop: 4 }}>
                      {stateBrackets.map((b, i) => (
                        <div key={i}>
                          {(b.rate * 100).toFixed(2)}% on {fmt(b.min)}–{b.max ? fmt(b.max) : '∞'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            }
          />

          {/* Step 5b: Local Income Tax (Phase 17) */}
          {localTaxEntry ? (
            <StepSection
              number="5b"
              title="Local Income Tax"
              content={
                <div>
                  <div style={{ fontSize: 13, marginBottom: 8, color: C.textMid }}>
                    <strong>Jurisdiction:</strong> {localTaxResolution.key}
                    {' '}
                    <span style={{ fontSize: 11, color: C.textLight }}>
                      (resolved via {localTaxResolution.location?.source || 'state'})
                    </span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 10, color: C.textMid }}>
                    <strong>Type:</strong> {localTaxEntry.type || '—'}
                    {' · '}
                    <strong>Conforms to federal:</strong> {localTaxEntry.conformsToFederal ? 'Yes' : 'No'}
                    {localTaxEntry.flatRate != null && (
                      <> {' · '}<strong>Rate:</strong> {(localTaxEntry.flatRate * 100).toFixed(2)}%</>
                    )}
                    {localTaxEntry.brackets && (
                      <> {' · '}<strong>Progressive brackets</strong></>
                    )}
                  </div>
                  {!localTaxEntry.conformsToFederal && (
                    <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#92400e' }}>
                      ⚠️ This local jurisdiction does not conform to federal pre-tax treatment. Local tax is calculated on full salary; Section 125 produces no local-tax savings.
                    </div>
                  )}
                  <ComparisonTable>
                    <ComparisonRow
                      label="Local Income Tax"
                      without={fmt(calc.without.localTax * 12)}
                      with={fmt(calc.with125.localTax * 12)}
                      highlight
                    />
                  </ComparisonTable>
                  <SavingsBox value={calc.diff.localTax * 12} label="Local Income Tax Savings" />
                </div>
              }
            />
          ) : null}

          {/* Step 6: State Payroll Taxes (if applicable) */}
          {(sdiRate > 0 || pfmlRate > 0 || fliRate > 0) && (
            <StepSection
              number={6}
              title="State Payroll Taxes"
              content={
                <div>
                  {sdiRate > 0 && (
                    <>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        <strong>SDI Rate:</strong> {(sdiRate * 100).toFixed(2)}%
                      </div>
                      <ComparisonTable>
                        <ComparisonRow 
                          label="SDI Tax" 
                          without={fmt(calc.without.sdi * 12)} 
                          with={fmt(calc.with125.sdi * 12)} 
                          highlight
                        />
                      </ComparisonTable>
                      <SavingsBox value={calc.diff.sdi * 12} label="SDI Savings" />
                    </>
                  )}

                  {pfmlRate > 0 && (
                    <>
                      <div style={{ fontSize: 13, marginTop: 12, marginBottom: 8 }}>
                        <strong>PFML Rate:</strong> {(pfmlRate * 100).toFixed(2)}%
                      </div>
                      <ComparisonTable>
                        <ComparisonRow 
                          label="PFML Tax" 
                          without={fmt(calc.without.pfml * 12)} 
                          with={fmt(calc.with125.pfml * 12)} 
                          highlight
                        />
                      </ComparisonTable>
                      <SavingsBox value={calc.diff.pfml * 12} label="PFML Savings" />
                    </>
                  )}

                  {fliRate > 0 && (
                    <>
                      <div style={{ fontSize: 13, marginTop: 12, marginBottom: 8 }}>
                        <strong>FLI Rate:</strong> {(fliRate * 100).toFixed(2)}%
                      </div>
                      <ComparisonTable>
                        <ComparisonRow 
                          label="FLI Tax" 
                          without={fmt(calc.without.fli * 12)} 
                          with={fmt(calc.with125.fli * 12)} 
                          highlight
                        />
                      </ComparisonTable>
                      <SavingsBox value={calc.diff.fli * 12} label="FLI Savings" />
                    </>
                  )}
                </div>
              }
            />
          )}

          {/* Step 7: Total Tax Savings */}
          <StepSection
            number={7}
            title="Total Tax Savings"
            content={
              <div style={{
                padding: 16,
                background: C.greenLight,
                border: `2px solid ${C.green}`,
                borderRadius: 8,
                fontSize: 13,
                ...S.mono
              }}>
                <Row label="Federal Income Tax Savings:" value={fmt(calc.diff.federalIncomeTax)} />
                <Row label="Social Security Savings:" value={fmt(calc.diff.socialSecurity)} />
                <Row label="Medicare Savings:" value={fmt(calc.diff.medicare)} />
                <Row label="State Income Tax Savings:" value={fmt(calc.diff.stateIncomeTax)} />
                {sdiRate > 0 && <Row label="SDI Savings:" value={fmt(calc.diff.sdi * 12)} />}
                {pfmlRate > 0 && <Row label="PFML Savings:" value={fmt(calc.diff.pfml * 12)} />}
                {fliRate > 0 && <Row label="FLI Savings:" value={fmt(calc.diff.fli * 12)} />}
                {localTaxEntry && (
                  <Row label={`Local Tax Savings (${localTaxResolution.key}):`} value={fmt(calc.diff.localTax * 12)} />
                )}
                <div style={{ 
                  borderTop: `1px solid ${C.green}`, 
                  marginTop: 8, 
                  paddingTop: 8,
                  fontWeight: 700,
                  fontSize: 14
                }}>
                  <Row label="TOTAL ANNUAL TAX SAVINGS:" value={fmt(calc.diff.totalTax * 12)} />
                  <Row label="Total Monthly Tax Savings:" value={fmt(calc.diff.totalTax / 12)} />
                </div>
              </div>
            }
          />

          {/* Step 8: Employee Net Benefit */}
          <StepSection
            number={8}
            title="Employee Net Benefit"
            content={
              <div>
                <FormulaBox>
                  Employee Net = Total Tax Savings − Annual Spread
                  <br />
                  = {fmt(calc.diff.totalTax * 12)} − {fmt(annualSpread)} = {fmt(calc.diff.netTakeHome * 12)}/year
                </FormulaBox>

                <div style={{
                  padding: 16,
                  background: calc.diff.netTakeHome >= 0 ? C.greenLight : C.redLight,
                  border: `2px solid ${calc.diff.netTakeHome >= 0 ? C.green : C.red}`,
                  borderRadius: 8,
                  marginTop: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  textAlign: 'center',
                  ...S.mono
                }}>
                  {calc.diff.netTakeHome >= 0 ? '✅' : '❌'} Employee Annual Net: {fmt(calc.diff.netTakeHome * 12)}
                  <br />
                  <span style={{ fontSize: 12, fontWeight: 500 }}>
                    ({fmt(calc.diff.netTakeHome / 12)}/month)
                  </span>
                </div>
              </div>
            }
          />

          {/* Step 9: Employer FICA Savings */}
          <StepSection
            number={9}
            title="Employer FICA Savings"
            content={
              <div>
                <FormulaBox>
                  Employer FICA = Annual Premium × Employer FICA Rate
                  <br />
                  = {fmt(annualPremium)} × {(employerFicaRate * 100).toFixed(3)}% = {fmt(calc.employerFicaSavingsPerPeriod * 12)}/year
                </FormulaBox>

                <div style={{
                  padding: 12,
                  background: C.accentBg,
                  borderRadius: 6,
                  fontSize: 12,
                  marginTop: 12,
                  fontStyle: 'italic',
                  color: C.textMid
                }}>
                  Note: Zero cost to employer — this is pure FICA savings on the reduced payroll
                </div>
              </div>
            }
          />

          {/* Step 10: Viability Checks */}
          <StepSection
            number={10}
            title="Viability Checks"
            content={
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                    Minimum Wage Check:
                  </div>
                  <div style={{ fontSize: 13, ...S.mono, marginLeft: 12 }}>
                    <Row label="Residual Income:" value={`${fmt(salary)} − ${fmt(annualPremium)} = ${fmt(residualIncome)}`} />
                    <Row label="Effective Hourly:" value={`${fmt(residualIncome)} ÷ ${annualHours} = ${fmt(effectiveHourly)}/hr`} />
                    <div style={{ padding: 8, background: C.bg, borderRadius: 6, margin: '6px 0', fontSize: 12 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, color: C.textMid }}>Applicable minimum wage</div>
                      <Row label="Federal floor:" value={`${fmt(minWageInfo.federal)}/hr`} />
                      <Row label={`State floor (${state}):`} value={minWageInfo.state ? `${fmt(minWageInfo.state)}/hr` : '— (no override)'} />
                      <Row label={`City floor${minWageInfo.jurisdictionKey ? ` (${minWageInfo.jurisdictionKey})` : ''}:`} value={minWageInfo.city ? `${fmt(minWageInfo.city)}/hr` : '— (none)'} />
                      <Row
                        label="→ Applied:"
                        value={
                          <span style={{ color: C.accent, fontWeight: 700 }}>
                            {fmt(minWage)}/hr ({minWageInfo.basis})
                          </span>
                        }
                      />
                    </div>
                    <Row 
                      label="Result:" 
                      value={
                        <span style={{ color: wageOk ? C.green : C.red, fontWeight: 700 }}>
                          {wageOk ? '✅ PASS' : '❌ FAIL'} ({fmt(effectiveHourly)} {wageOk ? '≥' : '<'} {fmt(minWage)})
                        </span>
                      } 
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                    Net Positive Check:
                  </div>
                  <div style={{ fontSize: 13, ...S.mono, marginLeft: 12 }}>
                    <Row label="Employee Annual Net:" value={fmt(calc.diff.netTakeHome * 12)} />
                    <Row 
                      label="Result:" 
                      value={
                        <span style={{ color: netPositive ? C.green : C.red, fontWeight: 700 }}>
                          {netPositive ? '✅ PASS' : '❌ FAIL'} (employee {netPositive ? 'benefits' : 'loses'} financially)
                        </span>
                      } 
                    />
                  </div>
                </div>

                <div style={{
                  padding: 12,
                  background: viable ? C.greenLight : C.redLight,
                  border: `2px solid ${viable ? C.green : C.red}`,
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  textAlign: 'center'
                }}>
                  PLAN VIABLE? {viable ? '✅ YES' : '❌ NO'} — {viable ? 'Eligible for enrollment' : 'Not eligible'}
                </div>
              </div>
            }
          />

          {/* Step 11: Optimization Logic */}
          <StepSection
            number={11}
            title="Optimization Logic"
            content={
              <div>
                <div style={{ fontSize: 13, marginBottom: 12, color: C.textMid }}>
                  The optimizer tests all {activePlans.length} plan tiers and:
                  <ol style={{ marginLeft: 20, marginTop: 8 }}>
                    <li>Filters to viable plans (net positive AND above minimum wage)</li>
                    <li>Among viable plans, selects highest annual employee benefit</li>
                    <li>If no plan viable, calculates break-even salary</li>
                  </ol>
                </div>

                <div style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  overflow: 'hidden'
                }}>
                  {allPlanResults.map((plan, i) => {
                    const isOptimal = plan.id === selectedPlan.id;
                    return (
                      <div
                        key={plan.id}
                        style={{
                          padding: '10px 12px',
                          background: isOptimal ? C.accentBg : (i % 2 === 0 ? '#fff' : C.bg),
                          borderBottom: i < allPlanResults.length - 1 ? `1px solid ${C.border}` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          fontSize: 13,
                          ...S.mono
                        }}
                      >
                        <span style={{ width: 20 }}>
                          {isOptimal ? '⭐' : plan.viable ? '✅' : '❌'}
                        </span>
                        <span style={{ flex: 1, fontWeight: isOptimal ? 700 : 500 }}>
                          {plan.label}
                        </span>
                        <span style={{ 
                          width: 120, 
                          textAlign: 'right',
                          color: plan.annualNet >= 0 ? C.green : C.red,
                          fontWeight: 600
                        }}>
                          {fmt(plan.annualNet)}/yr
                        </span>
                        <span style={{ 
                          width: 100,
                          fontSize: 11,
                          color: plan.viable ? C.green : C.textMid
                        }}>
                          {plan.viable ? 'viable' : plan.netPositive ? 'min wage fail' : 'net negative'}
                        </span>
                        {isOptimal && (
                          <span style={{
                            padding: '2px 8px',
                            background: C.accent,
                            color: '#fff',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700
                          }}>
                            OPTIMAL
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            }
          />

          {/* Footer Note */}
          <div style={{
            marginTop: 20,
            padding: 12,
            background: C.bg,
            borderRadius: 8,
            fontSize: 11,
            color: C.textMid,
            lineHeight: 1.6
          }}>
            <strong>Audit note:</strong> All calculations use annual figures. Monthly amounts shown 
            in the app are annual ÷ 12. Pay frequency only affects display, never the underlying math. 
            State conformity determines whether pre-tax premium reduces state income tax. SDI/PFML/FLI 
            always conform in all states. All rates and brackets shown are from the current admin 
            configuration (last synced: <strong>{lastSyncDate}</strong>).
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function StepSection({ number, title, content }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        padding: '8px 12px',
        background: C.accent,
        color: '#fff',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 12
      }}>
        Step {number}: {title}
      </div>
      <div style={{ paddingLeft: 12 }}>
        {content}
      </div>
    </div>
  );
}

function Row({ label, value, note, highlight }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      background: highlight ? C.accentBg : 'transparent',
      paddingLeft: highlight ? 8 : 0,
      paddingRight: highlight ? 8 : 0,
      borderRadius: 4
    }}>
      <span style={{ color: C.textMid }}>{label}</span>
      <span style={{ fontWeight: 600, marginLeft: 12 }}>
        {value}
        {note && <span style={{ fontWeight: 400, color: C.textMid, marginLeft: 8 }}>{note}</span>}
      </span>
    </div>
  );
}

function FormulaBox({ children }) {
  return (
    <div style={{
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      fontSize: 13,
      ...S.mono,
      marginBottom: 12
    }}>
      {children}
    </div>
  );
}

function ComparisonTable({ children }) {
  return (
    <div style={{
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      overflow: 'hidden',
      marginBottom: 12
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        fontSize: 11,
        fontWeight: 700,
        background: C.bg,
        padding: '8px 12px',
        borderBottom: `1px solid ${C.border}`,
        color: C.textMid,
        textTransform: 'uppercase'
      }}>
        <div></div>
        <div style={{ textAlign: 'right' }}>WITHOUT HC</div>
        <div style={{ textAlign: 'right' }}>WITH HC</div>
      </div>
      {children}
    </div>
  );
}

function ComparisonRow({ label, without, with: withValue, highlight }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr',
      padding: '8px 12px',
      fontSize: 13,
      ...S.mono,
      background: highlight ? C.accentBg : '#fff',
      borderBottom: `1px solid ${C.borderLight}`
    }}>
      <div style={{ color: C.textMid }}>{label}</div>
      <div style={{ textAlign: 'right', fontWeight: 600 }}>{without}</div>
      <div style={{ textAlign: 'right', fontWeight: 600, color: highlight ? C.accent : C.text }}>{withValue}</div>
    </div>
  );
}

function SavingsBox({ value, label }) {
  return (
    <div style={{
      padding: 10,
      background: C.greenLight,
      border: `1px solid ${C.green}`,
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 700,
      color: C.green,
      textAlign: 'center',
      marginTop: 8
    }}>
      {label}: {fmt(value)}
    </div>
  );
}
